import { LOGIN_TOKEN } from '@/global/constants'
import { useBreadcrumbStore } from '@/store/breadcrumb'
import useLoginStore from '@/store/login/login'
import { localCache } from '@/utils/cache'
import {
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw
} from 'vue-router'
import authRoutes from './modules/auth'
import courseRoutes from './modules/course'
import examRoutes from './modules/exam'
import homeRoutes from './modules/home'
import learnDataRoutes from './modules/learnData'
import personalRoutes from './modules/personal'
import studyRoutes from './modules/study'
import {
  AUTH_PAGE_PATHS,
  shouldClearAnonymousRegistrationState,
  shouldRedirectAuthenticatedAuthPage
} from './auth-page-redirect'

const publicRoutePaths = new Set([
  '/login',
  '/register',
  '/register-invite',
  '/reset-pass',
  '/retrieve-pass',
  '/mock-login'
])
const isLoggedInResetPassRoute = (route: { path: string; query?: any }) => {
  return (
    route.path.split('?')[0] === '/reset-pass' &&
    route.query?.mode === 'logged-in'
  )
}
const isPublicRoute = (route: {
  path: string
  name?: unknown
  meta: RouteRecordRaw['meta']
}) => {
  const path = route.path.split('?')[0]
  return (
    route.meta?.public === true ||
    publicRoutePaths.has(path) ||
    route.name === '404'
  )
}

// 🌟 关键：收集所有带breadcrumb配置的路由（全局索引）
const allRoutesWithBreadcrumb: Record<string, RouteRecordRaw> = {}
// 合并所有路由模块
const allRoutes = [
  ...authRoutes,
  ...homeRoutes,
  ...personalRoutes,
  ...studyRoutes,
  ...courseRoutes,
  ...examRoutes,
  ...learnDataRoutes
]
// 构建路由path到路由配置的映射
allRoutes.forEach((route) => {
  if (route.meta?.breadcrumb) {
    let fullPath = route.path
    if (!fullPath.startsWith('/')) {
      fullPath = `/${fullPath}`
    }
    fullPath = fullPath.replace(/\/\/+/g, '/')
    allRoutesWithBreadcrumb[fullPath] = route
    // console.log('面包屑路由映射:', fullPath, '→', route.meta.breadcrumb.label)
  }
})

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    redirect: '/home',
    children: [
      ...authRoutes,
      ...homeRoutes,
      ...personalRoutes,
      ...studyRoutes,
      ...courseRoutes,
      ...examRoutes,
      ...learnDataRoutes
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: () => import('@/views/not-found/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  routes
})

const splitPathToLevels = (fullPath: string) => {
  const pathOnly = fullPath.split('?')[0]

  const levels: string[] = []
  const segments = pathOnly.split('/').filter((seg) => seg)
  let currentPath = ''
  segments.forEach((seg) => {
    currentPath += `/${seg}`
    levels.push(currentPath)
  })
  // console.log('路径拆分结果:', fullPath, '→', levels)
  return levels
}

router.beforeEach(async (to, from, next) => {
  // console.log('路由跳转:', {
  //   从: from.fullPath,
  //   到: to.fullPath,
  //   匹配的路由: to.matched.map((route) => ({
  //     path: route.path,
  //     name: route.name,
  //     component: route.components
  //   }))
  // })

  const breadcrumbStore = useBreadcrumbStore()
  const breadcrumbList = []

  const pathLevels = splitPathToLevels(to.fullPath)
  pathLevels.forEach((path, index) => {
    const route = allRoutesWithBreadcrumb[path]
    // console.log(
    //   '匹配面包屑:',
    //   path,
    //   '→',
    //   route ? route.meta.breadcrumb.label : '未匹配到'
    // )
    if (route?.meta?.breadcrumb) {
      breadcrumbList.push({
        label: route.meta.breadcrumb.label || route.meta.title,
        path: route.meta.breadcrumb.path || path,
        query: route.meta.breadcrumb.query,
        disabled: index === pathLevels.length - 1
      })
    }
  })

  // console.log('最终面包屑列表:', breadcrumbList)

  breadcrumbStore.setBreadcrumbList(breadcrumbList)
  const loginStore = useLoginStore()
  const token = localCache.getCache(LOGIN_TOKEN)
  const currentPath = to.path.split('?')[0]
  const isLoggedInResetPass = isLoggedInResetPassRoute(to)

  if (isLoggedInResetPass && !token) {
    loginStore.clearUserInfo()
    loginStore.setRedirectPath('')
    loginStore.closeLoginModal()
    next('/login')
    return
  }

  if (
    shouldClearAnonymousRegistrationState({
      currentPath,
      hasToken: Boolean(token),
      isLoggedInResetPass
    })
  ) {
    loginStore.clearUserInfo()
    loginStore.setRedirectPath('')
  }

  if (AUTH_PAGE_PATHS.has(currentPath) && loginStore.loginModalVisible) {
    loginStore.closeLoginModal()
  }

  if (
    shouldRedirectAuthenticatedAuthPage({
      currentPath,
      hasToken: Boolean(token),
      isLoggedInResetPass
    })
  ) {
    const redirectPath = loginStore.redirectPath
    const redirectPathOnly = redirectPath.split('?')[0]
    const targetPath =
      redirectPath && !AUTH_PAGE_PATHS.has(redirectPathOnly)
        ? redirectPath
        : '/home'

    loginStore.setRedirectPath('')
    next(targetPath)
    return
  }

  if (!token && !isPublicRoute(to)) {
    loginStore.clearUserInfo()
    loginStore.setRedirectPath(to.fullPath)
    loginStore.closeLoginModal()
    next('/login')
    return
  }

  next()
})

const isPersonalRoute = (route: { matched: readonly { name?: unknown }[] }) =>
  route.matched.some((record) => record.name === 'Personal')

router.afterEach((to, from) => {
  if (isPersonalRoute(to) && !isPersonalRoute(from)) {
    const loginStore = useLoginStore()
    const token = loginStore.token || localCache.getCache(LOGIN_TOKEN)

    if (token) {
      void loginStore.fetchPlatformInfo(token).catch((error) => {
        console.error('进入个人中心时刷新学籍信息失败：', error)
      })
    }
  }
})

export default router
